import { db } from '../Config/firebase';
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc
} from 'firebase/firestore';
import { 
  Syllabus, 
  Level
} from '../state/subscriptionTableState/subscriptionTableSlice';
import { v4 as uuidv4 } from 'uuid';

export const SyllabusService = {
  /**
   * Get all syllabi for a service provider
   */
  getAllSyllabi: async (providerId: string): Promise<Syllabus[]> => {
    try {
      const syllabusCollection = collection(db, 'ServiceProviders', providerId, 'Syllabi');
      const snapshot = await getDocs(syllabusCollection);
      
      const syllabi: Syllabus[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data() as Syllabus;
        syllabi.push({
          ...data,
          id: doc.id
        });
      });
      
      return syllabi;
    } catch (error) {
      console.error('Error getting syllabi:', error);
      throw error;
    }
  },
  
  /**
   * Get a single syllabus by ID
   */
  getSyllabus: async (providerId: string, syllabusId: string): Promise<Syllabus | null> => {
    try {
      const syllabusRef = doc(db, 'ServiceProviders', providerId, 'Syllabi', syllabusId);
      const snapshot = await getDoc(syllabusRef);
      
      if (snapshot.exists()) {
        return {
          ...snapshot.data() as Syllabus,
          id: snapshot.id
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error getting syllabus:', error);
      throw error;
    }
  },
  
  /**
   * Create a new syllabus for a service provider
   */
  createSyllabus: async (providerId: string, syllabus: Omit<Syllabus, 'id'>): Promise<Syllabus> => {
    try {
      if (!providerId) {
        console.error('Error creating syllabus: providerId is missing or invalid');
        throw new Error('Provider ID is required to create a syllabus');
      }

      console.log(`Creating syllabus for provider ${providerId}`);
      
      // Ensure all nested objects have proper structure to avoid data loss
      const processedLevels = syllabus.levels.map(level => ({
        ...level,
        sessions: (level.sessions || []).map(session => ({
          ...session,
          topics: (session.topics || []).map(topic => ({
            ...topic,
            subtopics: topic.subtopics || [],
            timeLogs: topic.timeLogs || []
          }))
        }))
      }));
      
      const newSyllabus: Syllabus = {
        ...syllabus,
        levels: processedLevels,
        id: uuidv4(),
        createdAt: Date.now(),
        lastModified: Date.now()
      };
      
      // Create the full path to the document for better logging
      const docPath = `ServiceProviders/${providerId}/Syllabi/${newSyllabus.id}`;
      console.log(`Saving syllabus to path: ${docPath}`);
      
      // Use the setDoc method to create the document with the specified ID
      await setDoc(doc(db, 'ServiceProviders', providerId, 'Syllabi', newSyllabus.id), newSyllabus);
      
      console.log(`Successfully created syllabus with ID: ${newSyllabus.id}`);
      return newSyllabus;
    } catch (error) {
      console.error('Error creating syllabus:', error);
      
      // More detailed error message based on Firebase error codes
      if (error instanceof Error) {
        const errorMessage = error.message;
        if (errorMessage.includes('permission-denied')) {
          throw new Error(`Permission denied. User with ID ${providerId} may not have write access to create syllabi.`);
        } else if (errorMessage.includes('not-found')) {
          throw new Error(`Service provider with ID ${providerId} not found.`);
        }
      }
      
      throw error;
    }
  },
  
  /**
   * Update a syllabus
   */
  updateSyllabus: async (providerId: string, syllabusId: string, updates: Partial<Syllabus>): Promise<void> => {
    try {
      if (!providerId) {
        console.error('Error updating syllabus: providerId is missing or invalid');
        throw new Error('Provider ID is required to update a syllabus');
      }
      
      if (!syllabusId) {
        console.error('Error updating syllabus: syllabusId is missing or invalid');
        throw new Error('Syllabus ID is required to update a syllabus');
      }

      console.log(`Updating syllabus ${syllabusId} for provider ${providerId}`);
      
      // Create the full path to the document for better logging
      const docPath = `ServiceProviders/${providerId}/Syllabi/${syllabusId}`;
      console.log(`Updating syllabus at path: ${docPath}`);
      
      // First check if the document exists
      const syllabusRef = doc(db, 'ServiceProviders', providerId, 'Syllabi', syllabusId);
      const docSnapshot = await getDoc(syllabusRef);
      
      if (!docSnapshot.exists()) {
        console.error(`Syllabus with ID ${syllabusId} does not exist for provider ${providerId}`);
        throw new Error(`Syllabus not found. It may have been deleted.`);
      }
      
      // Process levels to ensure all nested objects have proper structure
      let processedUpdates = { ...updates };
      
      if (updates.levels) {
        const processedLevels = updates.levels.map(level => ({
          ...level,
          sessions: (level.sessions || []).map(session => ({
            ...session,
            topics: (session.topics || []).map(topic => ({
              ...topic,
              subtopics: topic.subtopics || [],
              timeLogs: topic.timeLogs || []
            }))
          }))
        }));
        
        processedUpdates = {
          ...processedUpdates,
          levels: processedLevels
        };
      }
      
      // Include the lastModified timestamp
      const updateData = {
        ...processedUpdates,
        lastModified: Date.now()
      };
      
      await updateDoc(syllabusRef, updateData);
      console.log(`Successfully updated syllabus with ID: ${syllabusId}`);
    } catch (error) {
      console.error('Error updating syllabus:', error);
      
      // More detailed error message based on Firebase error codes
      if (error instanceof Error) {
        const errorMessage = error.message;
        if (errorMessage.includes('permission-denied')) {
          throw new Error(`Permission denied. User with ID ${providerId} may not have write access to update syllabi.`);
        } else if (errorMessage.includes('not-found')) {
          throw new Error(`Syllabus with ID ${syllabusId} or provider with ID ${providerId} not found.`);
        }
      }
      
      throw error;
    }
  },
  
  /**
   * Delete a syllabus
   */
  deleteSyllabus: async (providerId: string, syllabusId: string): Promise<void> => {
    try {
      const syllabusRef = doc(db, 'ServiceProviders', providerId, 'Syllabi', syllabusId);
      await deleteDoc(syllabusRef);
    } catch (error) {
      console.error('Error deleting syllabus:', error);
      throw error;
    }
  },
  
  /**
   * Copy a syllabus to a subscription
   */
  copySyllabusToSubscription: async (
    providerId: string, 
    syllabusId: string, 
    subscriptionId: string
  ): Promise<Syllabus> => {
    try {
      // Get the master syllabus
      const syllabus = await SyllabusService.getSyllabus(providerId, syllabusId);
      
      if (!syllabus) {
        throw new Error('Syllabus not found');
      }
      
      // Create a deep copy with a new ID
      const subscriptionSyllabus: Syllabus = {
        ...JSON.parse(JSON.stringify(syllabus)),
        id: uuidv4(),
        lastModified: Date.now()
      };
      
      // Update the subscription document
      const subscriptionRef = doc(db, 'ServiceProviders', providerId, 'Subscriptions', subscriptionId);
      await updateDoc(subscriptionRef, {
        syllabus: subscriptionSyllabus
      });
      
      return subscriptionSyllabus;
    } catch (error) {
      console.error('Error copying syllabus to subscription:', error);
      throw error;
    }
  },
  
  /**
   * Add a level to a master syllabus
   */
  addLevel: async (providerId: string, syllabusId: string, level: Omit<Level, 'id'>): Promise<Level> => {
    try {
      const syllabusRef = doc(db, 'ServiceProviders', providerId, 'Syllabi', syllabusId);
      const syllabusDoc = await getDoc(syllabusRef);
      
      if (!syllabusDoc.exists()) {
        throw new Error('Syllabus not found');
      }
      
      const syllabus = syllabusDoc.data() as Syllabus;
      
      const newLevel: Level = {
        ...level,
        id: uuidv4(),
        sessions: []
      };
      
      const levels = syllabus.levels || [];
      
      await updateDoc(syllabusRef, {
        levels: [...levels, newLevel],
        lastModified: Date.now()
      });
      
      return newLevel;
    } catch (error) {
      console.error('Error adding level:', error);
      throw error;
    }
  },
  
  /**
   * Update a level in a master syllabus
   */
  updateLevel: async (
    providerId: string, 
    syllabusId: string, 
    levelId: string, 
    updates: Partial<Level>
  ): Promise<void> => {
    try {
      const syllabusRef = doc(db, 'ServiceProviders', providerId, 'Syllabi', syllabusId);
      const syllabusDoc = await getDoc(syllabusRef);
      
      if (!syllabusDoc.exists()) {
        throw new Error('Syllabus not found');
      }
      
      const syllabus = syllabusDoc.data() as Syllabus;
      const levels = syllabus.levels || [];
      
      const updatedLevels = levels.map(level => 
        level.id === levelId 
          ? { ...level, ...updates } 
          : level
      );
      
      await updateDoc(syllabusRef, {
        levels: updatedLevels,
        lastModified: Date.now()
      });
    } catch (error) {
      console.error('Error updating level:', error);
      throw error;
    }
  },
  
  /**
   * Delete a level from a master syllabus
   */
  deleteLevel: async (providerId: string, syllabusId: string, levelId: string): Promise<void> => {
    try {
      const syllabusRef = doc(db, 'ServiceProviders', providerId, 'Syllabi', syllabusId);
      const syllabusDoc = await getDoc(syllabusRef);
      
      if (!syllabusDoc.exists()) {
        throw new Error('Syllabus not found');
      }
      
      const syllabus = syllabusDoc.data() as Syllabus;
      const levels = syllabus.levels || [];
      
      const updatedLevels = levels.filter(level => level.id !== levelId);
      
      await updateDoc(syllabusRef, {
        levels: updatedLevels,
        lastModified: Date.now()
      });
    } catch (error) {
      console.error('Error deleting level:', error);
      throw error;
    }
  }
};

export default SyllabusService; 